import { useEffect, useState } from 'react';
import { EvieOrb } from '@/components/primitives';

interface TourOrbProps {
  /** 0 = greet (centered, large); 1 = last step (docked top-right, ~40px). */
  progress: number;
  reduced: boolean;
  /** When set, EVII flies to these viewport px coordinates instead of the
   *  progress-interpolated path — used to "touch" a step's orbAnchor element
   *  (e.g. the rewards row). Null returns her to the path. */
  overridePx?: { x: number; y: number } | null;
}

const BASE = 160;
const DOCKED = 40;

/**
 * EVII's orb for the tour: fades in slowly, centered and large at the greet,
 * shrinking toward the top-right launcher corner as the steps run. A fixed
 * wrapper that CSS-transforms (translate + scale) between the two poses; the
 * leaf EvieOrb keeps its own loop-vs-poster reduced-motion swap. Under
 * reduced-motion the wrapper snaps (no fade, no transition) to the current
 * pose.
 */
export function TourOrb({ progress, reduced, overridePx = null }: TourOrbProps) {
  // Slow fade-in on first appearance (Warren: EVII fades in over the map).
  // Start transparent, flip to visible on the next frame so the opacity
  // transition runs. Reduced-motion skips the fade entirely.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const p = Math.max(0, Math.min(1, progress));
  // center of viewport at greet → near the top-right launcher as steps run
  const cx = 50 + (88 - 50) * p; // vw
  const cy = 34 + (5 - 34) * p; // vh (greet sits a touch above center)
  const pathScale = 1 + (DOCKED / BASE - 1) * p; // 1 → ~0.31
  // Anchor override: fly to the element's coordinates at a small,
  // "touching" size; otherwise follow the interpolated path.
  const left = overridePx ? `${overridePx.x}px` : `${cx}vw`;
  const top = overridePx ? `${overridePx.y}px` : `${cy}vh`;
  const scale = overridePx ? 0.42 : pathScale;

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left,
        top,
        width: BASE,
        height: BASE,
        transform: `translate(-50%, -50%) scale(${scale})`,
        transformOrigin: 'center',
        opacity: reduced || visible ? 1 : 0,
        transition: reduced
          ? 'none'
          : 'opacity 2400ms ease, left 420ms ease, top 420ms ease, transform 420ms ease',
        pointerEvents: 'none',
        filter: 'drop-shadow(0 10px 26px rgba(0, 0, 0, 0.4))',
        zIndex: 2,
      }}
    >
      <EvieOrb size={BASE} />
    </div>
  );
}
